/**
 * Onboarding · orchestrator for the new-account onboarding experience.
 * slot_onboarding_redesign OB.3 (23 MAY 2026).
 *
 * Replaces the retired `Tour.tsx` + `tour-steps.ts` Sprint 0 spotlight tour.
 * Mounted once at App level (like the old <Tour/>). Owns no routing — it
 * receives navigation callbacks and reads `progress` + onboarding-local state.
 *
 * Flow rework (slot_t00_ftue_rewrite · Genie 7 JUN 2026): the tutorial is the
 * step AFTER the first ticket, not before it —
 *   1. Welcome «Знакомство со студией» → «Открыть тикет» → T00 (сам учит
 *      механике тикета: галочки → вопрос → кнопка).
 *   2. T00 closed → «Карта студии» pops immediately (right on the ticket
 *      screen · Genie pick «а») or on the next Dashboard visit.
 *   3. «Первые шаги» checklist (post-T00 actions) + three contextual hints.
 * Welcome skip («я уже всё это знаю») suppresses all beats (`state.skipped`),
 * still closing T00 (App.tsx).
 *
 * Sequencing: the checklist + hints only arm once the Studio Map is dismissed,
 * so the student never sees two onboarding layers at once.
 */

import './onboarding.css';
import { useProgress } from '@/contexts/ProgressContext';
import { useOnboardingState } from './onboarding-state';
import { StudioMap } from './StudioMap';
import { FirstSteps } from './FirstSteps';
import { OnboardingHints } from './OnboardingHints';

/** Screens where the floating «Первые шаги» checklist is visible.
 *  Genie 7 JUN 2026: чеклист сам ведёт на Карту/Прогресс/Профиль — он обязан
 *  оставаться на экране, куда привёл, иначе студент теряет нить. */
const FIRST_STEPS_SCREENS: ReadonlySet<string> = new Set([
  'dashboard',
  'sprint',
  'ticket',
  'sprints',
  'progress',
  'profile',
]);

export interface OnboardingProps {
  /** Current App screen — drives Studio Map / checklist / hint visibility. */
  screen: string;
  /** Open the active sprint's Board (App.tsx `goSprint`). */
  onOpenBoard: () => void;
  /** Open the course map / sprints timeline (App.tsx `goSprints`). */
  onOpenMap: () => void;
  /** Open the Glossary overlay (App.tsx `openGlossary`). */
  onOpenGlossary: () => void;
}

export function Onboarding({ screen, onOpenBoard, onOpenMap, onOpenGlossary }: OnboardingProps) {
  const { progress } = useProgress();
  const { state, dismissStudioMap, hideFirstSteps, markGlossaryStep, markHintSeen } =
    useOnboardingState();

  // The onboarding only runs after the Welcome screen has been seen.
  if (!progress.onboarding.welcomeSeen) return null;
  // Welcome skip = know-it-all path → no tutorial at all (Genie 7 JUN).
  if (state.skipped) return null;
  // The tutorial is the step AFTER the first ticket: it arms only once T00
  // is closed (the ticket itself teaches the closing mechanics).
  if (progress.tickets['T00']?.status !== 'done') return null;

  // Genie pick «а» (7 JUN 2026): the Studio Map also pops right on the ticket
  // screen — the moment T00 is closed the student gets «что дальше» immediately,
  // instead of a dead end until the next Dashboard visit.
  const showMap =
    !state.studioMapDismissed && (screen === 'dashboard' || screen === 'ticket');
  const showSteps =
    state.studioMapDismissed && !state.firstStepsHidden && FIRST_STEPS_SCREENS.has(screen);
  const showHints = state.studioMapDismissed;

  return (
    <>
      {showMap && <StudioMap onDismiss={dismissStudioMap} onGo={onOpenBoard} />}
      {showSteps && (
        <FirstSteps
          onOpenBoard={onOpenBoard}
          onOpenMap={onOpenMap}
          onOpenGlossary={onOpenGlossary}
          mapVisited={state.hintsSeen.map}
          glossaryStepDone={state.glossaryStepDone}
          onGlossaryStep={markGlossaryStep}
          onHide={hideFirstSteps}
        />
      )}
      {showHints && (
        <OnboardingHints screen={screen} seen={state.hintsSeen} onSeen={markHintSeen} />
      )}
    </>
  );
}
